import type { CoreConfig } from '../config/env';
import { ApiError, NETWORK_ERROR_STATUS, TIMEOUT_ERROR_STATUS } from './errors';
import { buildQueryString } from './query-string';
import type { HttpClient, HttpMethod, RequestOptions, TokenStorage } from './types';

export interface HttpClientOptions {
  config: CoreConfig;
  tokenStorage?: TokenStorage;
  /** Chamado em 401 — permite ao app derrubar a sessão (PRJ-020 / PRJ-021). */
  onUnauthorized?: (error: ApiError) => void;
  /** Chamado em 403 — permite exibir tela/toast de acesso negado (PRJ-044). */
  onForbidden?: (error: ApiError) => void;
  /** Hook de observabilidade: toda requisição concluída passa por aqui. */
  onRequestFinished?: (info: RequestTelemetry) => void;
  /** Injetável em testes. */
  fetchFn?: typeof fetch;
  /** Gerador do correlation ID (PRJ-004). */
  generateCorrelationId?: () => string;
}

export interface RequestTelemetry {
  method: HttpMethod;
  path: string;
  status: number;
  durationMs: number;
  correlationId: string;
}

const JSON_CONTENT_TYPE = 'application/json';

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const {
    config,
    onUnauthorized,
    onForbidden,
    onRequestFinished,
    fetchFn = globalThis.fetch?.bind(globalThis),
    generateCorrelationId = defaultCorrelationId,
  } = options;

  if (!fetchFn) {
    throw new Error('fetch indisponível neste runtime — informe options.fetchFn.');
  }

  async function request<T>(
    method: HttpMethod,
    path: string,
    requestOptions: RequestOptions = {},
    asBlob = false,
  ): Promise<T> {
    const correlationId = generateCorrelationId();
    const url = `${trimSlash(config.apiBaseUrl)}${ensureLeadingSlash(path)}${buildQueryString(requestOptions.query)}`;

    const headers: Record<string, string> = {
      Accept: asBlob ? '*/*' : JSON_CONTENT_TYPE,
      'X-Correlation-Id': correlationId,
      'X-Client-Origin': config.clientOrigin,
      'X-Client-Version': config.clientVersion,
      ...requestOptions.headers,
    };

    if (requestOptions.idempotencyKey) {
      headers['Idempotency-Key'] = requestOptions.idempotencyKey;
    }

    const hasBody = requestOptions.body !== undefined && method !== 'GET';
    const isFormData =
      typeof FormData !== 'undefined' && requestOptions.body instanceof FormData;
    if (hasBody && !isFormData) headers['Content-Type'] = JSON_CONTENT_TYPE;

    const timeoutMs = requestOptions.timeoutMs ?? config.requestTimeoutMs;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const signal = mergeSignals(controller.signal, requestOptions.signal);

    const startedAt = Date.now();
    let response: Response;

    try {
      response = await fetchFn(url, {
        method,
        headers,
        body: hasBody
          ? isFormData
            ? requestOptions.body as FormData
            : JSON.stringify(requestOptions.body)
          : undefined,
        signal,
        credentials: 'include',
      });
    } catch (error) {
      clearTimeout(timeoutId);
      const aborted = controller.signal.aborted;
      throw new ApiError({
        status: aborted ? TIMEOUT_ERROR_STATUS : NETWORK_ERROR_STATUS,
        message: aborted
          ? 'A requisição excedeu o tempo limite.'
          : 'Não foi possível conectar ao servidor.',
        details: error,
        correlationId,
      });
    } finally {
      clearTimeout(timeoutId);
    }

    onRequestFinished?.({
      method,
      path,
      status: response.status,
      durationMs: Date.now() - startedAt,
      correlationId,
    });

    if (asBlob) {
      if (!response.ok) {
        const payload = await parseBody(response);
        const error = toApiError(response, payload, correlationId);
        if (error.isUnauthorized) onUnauthorized?.(error);
        if (error.isForbidden) onForbidden?.(error);
        throw error;
      }
      return (await response.blob()) as T;
    }

    if (response.status === 204) return undefined as T;

    const payload = await parseBody(response);

    if (!response.ok) {
      const error = toApiError(response, payload, correlationId);
      if (error.isUnauthorized) onUnauthorized?.(error);
      if (error.isForbidden) onForbidden?.(error);
      throw error;
    }

    return unwrap<T>(payload);
  }

  return {
    get: (path, opts) => request('GET', path, opts),
    getBlob: (path, opts) => request<Blob>('GET', path, opts, true),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    put: (path, body, opts) => request('PUT', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    delete: (path, opts) => request('DELETE', path, opts),
  };
}

async function parseBody(response: Response): Promise<unknown> {
  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    const text = await response.text();
    return text || null;
  }
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function toApiError(response: Response, payload: unknown, correlationId: string): ApiError {
  const body = isRecord(payload) ? payload : {};
  const error = isRecord(body.error) ? body.error : body;

  return new ApiError({
    status: response.status,
    code: typeof error.code === 'string' ? error.code : undefined,
    message:
      typeof error.message === 'string' ? error.message : `Falha na requisição (${response.status})`,
    details: error.details ?? error.errors,
    correlationId: response.headers.get('x-correlation-id') ?? correlationId,
  });
}

/** Aceita tanto `{ data: T }` quanto o payload cru, para não travar no contrato do backend. */
function unwrap<T>(payload: unknown): T {
  if (isRecord(payload) && 'data' in payload && !('id' in payload)) {
    return payload.data as T;
  }
  return payload as T;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function ensureLeadingSlash(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function trimSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}

function mergeSignals(primary: AbortSignal, secondary?: AbortSignal): AbortSignal {
  if (!secondary) return primary;
  if (typeof AbortSignal.any === 'function') return AbortSignal.any([primary, secondary]);

  const controller = new AbortController();
  const abort = () => controller.abort();
  primary.addEventListener('abort', abort, { once: true });
  secondary.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

function defaultCorrelationId(): string {
  if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
  return `cid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
